<template>
    <div class="tag-selector relative">
        <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">{{ label }}</label>

        <!-- Tags selecionadas -->
        <div class="flex flex-wrap gap-1 mb-2" v-if="selectedTags.length > 0">
            <span v-for="tag in selectedTags" :key="tag.id"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-white"
                :style="{ backgroundColor: tag.color }">
                {{ tag.name }}
                <button @click="removeTag(tag)" type="button" class="hover:opacity-75">
                    <XMarkIcon class="w-3 h-3" />
                </button>
            </span>
        </div>

        <!-- Input de busca -->
        <div class="relative">
            <input
                type="text"
                v-model="searchTerm"
                @focus="showDropdown = true"
                @input="onSearch"
                :placeholder="placeholder"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-sm"
            />
            <TagIcon class="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
        </div>

        <!-- Dropdown de sugestões -->
        <div v-if="showDropdown"
            class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
            <div class="max-h-48 overflow-y-auto">
                <div v-if="loading" class="p-3 text-sm text-gray-500">Carregando tags...</div>
                <template v-else>
                    <button
                        v-for="tag in filteredTags"
                        :key="tag.id"
                        @click="selectTag(tag)"
                        type="button"
                        class="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                    >
                        <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: tag.color }"></span>
                        <span class="flex-1">{{ tag.name }}</span>
                        <CheckIcon v-if="isSelected(tag)" class="w-4 h-4 text-green-500" />
                    </button>
                    <div v-if="filteredTags.length === 0 && allTags.length === 0" class="p-3 text-sm text-gray-500">
                        Nenhuma tag cadastrada
                    </div>
                    <div v-else-if="filteredTags.length === 0 && searchTerm" class="p-3 text-sm text-gray-500">
                        Nenhuma tag encontrada para "{{ searchTerm }}"
                    </div>
                </template>
            </div>
            <button type="button" @click="openManager"
                class="tag-manage-option w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100 font-medium">
                <PlusIcon class="w-4 h-4 flex-shrink-0" />
                Cadastrar nova tag
            </button>
        </div>

        <TagsManagerModal :open="showManager" @close="showManager = false" @changed="onTagsChanged" />

        <!-- Overlay para fechar dropdown -->
        <div v-if="showDropdown" class="fixed inset-0 z-40" @click="showDropdown = false"></div>
    </div>
</template>

<script>
import { TagIcon, XMarkIcon, CheckIcon, PlusIcon } from '@heroicons/vue/24/outline'
import TagsManagerModal from './TagsManagerModal.vue'

export default {
    name: 'TagSelector',
    components: {
        TagIcon,
        XMarkIcon,
        CheckIcon,
        PlusIcon,
        TagsManagerModal,
    },
    props: {
        // Tags já selecionadas (para edição)
        modelValue: {
            type: Array,
            default: () => []
        },
        // Label do campo
        label: {
            type: String,
            default: ''
        },
        // Placeholder do input
        placeholder: {
            type: String,
            default: 'Buscar tags...'
        },
        // Permite múltiplas seleções
        multiple: {
            type: Boolean,
            default: true
        },
        // ID do agente para filtrar tags (opcional)
        agentId: {
            type: String,
            default: null
        }
    },
    emits: ['update:modelValue', 'change'],
    data() {
        return {
            searchTerm: '',
            showDropdown: false,
            allTags: [],
            loading: false,
            showManager: false,
        }
    },
    computed: {
        selectedTags() {
            return this.modelValue || [];
        },
        filteredTags() {
            let tags = this.allTags;

            // Filtrar por termo de busca
            if (this.searchTerm) {
                const term = this.searchTerm.toLowerCase();
                tags = tags.filter(tag => tag.name.toLowerCase().includes(term));
            }

            // Se não permite múltiplos e já tem um selecionado, não mostra nada
            if (!this.multiple && this.selectedTags.length > 0) {
                return [];
            }

            return tags;
        }
    },
    methods: {
        async loadTags() {
            this.loading = true;
            try {
                const params = {};
                if (this.agentId) {
                    params.agents_id = this.agentId;
                }
                const response = await this.$ApiService.get('crm/tags', params);
                this.allTags = response?.rows || response?.data?.rows || response?.data || [];
            } catch (error) {
                console.error('Erro ao carregar tags:', error);
                this.allTags = [];
            } finally {
                this.loading = false;
            }
        },
        openManager() {
            this.showDropdown = false;
            this.showManager = true;
        },
        // Tags criadas/editadas/excluídas no popup: recarrega e tira da seleção as que não existem mais
        async onTagsChanged() {
            await this.loadTags();
            const byId = new Map(this.allTags.map(t => [t.id, t]));
            const refreshed = this.selectedTags.filter(t => byId.has(t.id)).map(t => byId.get(t.id));
            const changed = refreshed.length !== this.selectedTags.length
                || refreshed.some((t, i) => t.name !== this.selectedTags[i].name || t.color !== this.selectedTags[i].color);
            if (changed) {
                this.$emit('update:modelValue', refreshed);
                this.$emit('change', refreshed);
            }
        },
        onSearch() {
            this.showDropdown = true;
        },
        selectTag(tag) {
            if (this.isSelected(tag)) {
                this.removeTag(tag);
                return;
            }

            let newSelection;
            if (this.multiple) {
                newSelection = [...this.selectedTags, tag];
            } else {
                newSelection = [tag];
                this.showDropdown = false;
            }

            this.$emit('update:modelValue', newSelection);
            this.$emit('change', newSelection);
            this.searchTerm = '';
        },
        removeTag(tag) {
            const newSelection = this.selectedTags.filter(t => t.id !== tag.id);
            this.$emit('update:modelValue', newSelection);
            this.$emit('change', newSelection);
        },
        isSelected(tag) {
            return this.selectedTags.some(t => t.id === tag.id);
        },
        // Método público para limpar seleção
        clear() {
            this.$emit('update:modelValue', []);
            this.$emit('change', []);
            this.searchTerm = '';
        }
    },
    mounted() {
        this.loadTags();
    },
    watch: {
        agentId() {
            this.loadTags();
        }
    }
}
</script>

<style scoped>
.tag-selector {
    position: relative;
}
</style>
